<template>
  <div
    v-if="rtConfig"
    class="max-w-360 m-auto w-full h-full flex flex-col justify-center items-center"
  >
    <RtValueOverview
      v-model:mode="mode"
      :active-tab="activeTab"
      :trigger-press="triggerPress"
      :rt-level="rtLevel"
      :rt-switch="rtSwitch"
      :vibration="vibrationPress"
      :vibration-feedback-type="vibrationFeedbackType"
      :beep="beepPress"
      :trigger-status="triggerStatus"
      :calibration-state="calibrationState"
      @navigate="onOverviewNavigate"
    />

    <div class="w-full flex flex-wrap gap-4 justify-center">
      <Tabs v-model="activeTab" class="shrink-0 w-full flex flex-col items-center gap-4.5">
        <TabsList class="p-0 flex items-center gap-4 h-10">
          <TabsTrigger v-for="tab in rtTabs" :key="tab.value" :value="tab.value">
            <Button color="glass" :active="tab.value === activeTab" class="h-10">
              {{ tab.label }}
            </Button>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="travel">
          <RtTravelCard
            v-model:mode="mode"
            :trigger-press="triggerPress"
            :trigger-status="triggerStatus"
            :config="rtConfig.triggerTravel"
            :loading="loading"
            @commit="onFieldCommit"
          />
        </TabsContent>

        <TabsContent value="sensitivity">
          <RtSensitivityCard
            v-model:mode="mode"
            :parameters="parameters"
            :rt-switch="rtSwitch"
            :rt-level="rtLevel"
            :config="rtConfig.rtTriggerLevel"
            :loading="loading"
            @update:rt-switch="onRtSwitchUpdate"
            @update:rt-level="onRtLevelUpdate"
          />
        </TabsContent>

        <TabsContent value="tactile">
          <RtVibrationCard
            v-model:mode="mode"
            :vibration="vibrationPress"
            :vibration-feedback-type="vibrationFeedbackType"
            :config="rtConfig.vibration"
            :feedback-type-config="rtConfig.vibrationFeedbackType"
            :loading="loading"
            @commit="onFieldCommit"
            @update:vibration-feedback-type="setVibrationFeedbackType"
          />
        </TabsContent>

        <TabsContent value="sound">
          <RtBeepCard
            v-model:mode="mode"
            :beep="beepPress"
            :beep-volume="beepVolume"
            :sound-config="rtConfig.sound"
            :volume-config="rtConfig.volume"
            :loading="loading"
            @commit="onFieldCommit"
          />
        </TabsContent>

        <TabsContent value="config">
          <RtConfigCard
            :loading="loading"
            :apply-preset="applyPreset"
            :get-current-preset="getCurrentPreset"
          />
        </TabsContent>

        <TabsContent value="calibration">
          <RtCalibrationCard
            :phase="calibrationPhase"
            :left-state="calibrationState.left"
            :right-state="calibrationState.right"
            :busy="calibrationActionPending"
            @action="onCalibrationAction"
          />
        </TabsContent>
      </Tabs>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import RtValueOverview from './components/overview/RtValueOverview.vue'
  import RtTravelCard from './components/travel/RtTravelCard.vue'
  import RtSensitivityCard from './components/sensitivity/RtSensitivityCard.vue'
  import RtVibrationCard from './components/tactile/RtVibrationCard.vue'
  import RtBeepCard from './components/sound/RtBeepCard.vue'
  import RtCalibrationCard from './components/calibration/RtCalibrationCard.vue'
  import { useMouseRt } from '../composables/useMouseRt.ts'
  import { useParameters } from '../composables/useParameters'
  import { useMouseCalibration } from '../composables/useMouseCalibration'
  import {
    RT_BUTTONS,
    type CalibrationVisualState,
    type RtFieldKey,
    type RtSelectionMode,
  } from './types'
  import type { RtSide } from '@rapoo/device-sdk'
  import { Button, Tabs, TabsContent, TabsList, TabsTrigger, toast } from '@rapoo/element'
  import RtConfigCard from './components/rt-config/RtConfigCard.vue'

  type SideMap<T> = Partial<Record<RtSide, T>>
  type RtTabValue = 'travel' | 'sensitivity' | 'tactile' | 'sound' | 'config' | 'calibration'

  const { t } = useI18n()

  const rtTabs = computed<Array<{ value: RtTabValue; label: string }>>(() => [
    { value: 'travel', label: t('rt.tabs.triggerTravel') },
    { value: 'sensitivity', label: t('rt.tabs.rapidTrigger') },
    { value: 'tactile', label: t('rt.tabs.vibration') },
    { value: 'sound', label: t('rt.tabs.sound') },
    { value: 'config', label: t('rt.tabs.config') },
    { value: 'calibration', label: t('rt.tabs.calibration') },
  ])

  const activeTab = ref<RtTabValue>('travel')
  const mode = ref<RtSelectionMode>('both')
  const calibrationActionPending = ref(false)

  const {
    rtSwitch,
    triggerPress,
    rtLevel,
    vibrationPress,
    vibrationFeedbackType,
    beepPress,
    beepVolume,
    rtConfig,
    loading,
    triggerStatus,
    init,
    setField,
    setSwitch,
    setVibrationFeedbackType,
    applyPreset,
    getCurrentPreset,
    startTriggerStatusPolling,
    stopTriggerStatusPolling,
  } = useMouseRt()

  const parameters = useParameters()

  const {
    phase: calibrationPhase,
    leftPassed,
    rightPassed,
    leftTriggered,
    rightTriggered,
    start: startCalibration,
    save: saveCalibration,
    cancel: cancelCalibration,
    reset: resetCalibration,
  } = useMouseCalibration()

  const calibrationState = computed<Record<RtSide, CalibrationVisualState>>(() => {
    const isActive = calibrationPhase.value === 'running' || calibrationPhase.value === 'saving'
    if (!isActive) return { left: 'idle', right: 'idle' }

    return {
      left: leftPassed.value ? 'passed' : leftTriggered.value ? 'triggered' : 'idle',
      right: rightPassed.value ? 'passed' : rightTriggered.value ? 'triggered' : 'idle',
    }
  })

  // v-model 适配器已移除：子组件只会在值实际变化时 emit update 事件，
  // useMouseRt 内的 setSwitch / setField 也已按侧合并，无需在父层再做 diff。

  function onRtSwitchUpdate(payload: SideMap<boolean>) {
    for (const side of RT_BUTTONS) {
      const value = payload[side]
      if (value !== undefined) {
        setSwitch(side, value)
      }
    }
  }

  function onRtLevelUpdate(payload: SideMap<number>) {
    for (const side of RT_BUTTONS) {
      const value = payload[side]
      if (value !== undefined) {
        setField('rtLevel', side, value)
      }
    }
  }

  function onFieldCommit(payload: { field: RtFieldKey; mode: RtSelectionMode; value: number }) {
    setField(payload.field, payload.mode, payload.value)
  }

  /** 概览块点击：跳转到对应 Tab，并把模式切到点击的一侧 */
  function onOverviewNavigate(payload: { tab: RtTabValue; side: RtSide }) {
    activeTab.value = payload.tab
    mode.value = payload.side
  }

  /** 校准卡片的单按钮状态机：开始 → 取消；左右均通过后 → 保存。 */
  async function onCalibrationAction() {
    if (
      calibrationActionPending.value ||
      calibrationPhase.value === 'entering' ||
      calibrationPhase.value === 'saving'
    )
      return

    calibrationActionPending.value = true

    try {
      if (calibrationPhase.value === 'running') {
        if (leftPassed.value && rightPassed.value) {
          const saved = await saveCalibration()
          if (saved) {
            toast.success(t('rt.calibration.saved'))
            resetCalibration()
          }
        } else {
          await cancelCalibration()
          resetCalibration()
        }
        return
      }

      await startCalibration()
    } finally {
      calibrationActionPending.value = false
    }
  }

  /**
   * 仅在「按键触发行程」tab 启用 0xB5 实时轮询；
   * 切到其他 tab 立即停止并清空已读到的状态，避免在其他面板误用 stale 数据。
   */
  watch(
    activeTab,
    async (tab, previousTab) => {
      if (tab === 'travel') {
        startTriggerStatusPolling()
      } else {
        stopTriggerStatusPolling()
      }

      // 离开校准 Tab 时，校准中的数据一律不保存，并确保设备退出校准模式。
      if (previousTab === 'calibration' && tab !== 'calibration') {
        if (calibrationPhase.value === 'running' || calibrationPhase.value === 'entering') {
          await cancelCalibration()
        }
        if (calibrationPhase.value !== 'saving') resetCalibration()
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    init()
    parameters.actions.refresh()
  })

  onBeforeUnmount(() => {
    stopTriggerStatusPolling()
  })
</script>
